import $$ from 'dom7';
import { createDobCalendar } from './auth.js';

/*
 * In-app KYC and wallet limits (API-KYC.md, API-CHANGES-FOR-APP.md §20-21).
 *
 *   POST app/php/kyc.php  kycStatus=1  -> the customer's KYC (masked), canSubmit,
 *                                        photo limits and the `wallet` object
 *   POST app/php/kyc.php  submitKyc=1  -> multipart: five text fields, consent,
 *                                        four photos
 *
 * The same `wallet` object also arrives on every `userinfo`, which app.js
 * fetches on each page init. noteAccount() keeps the latest copy so the
 * Withdraw / Send money routes can be gated without a round trip of their own.
 *
 * Privacy (API-KYC.md §6): the PAN and Aadhaar typed into the form are never
 * written anywhere -- no localStorage, no logs, no `name` attributes (so even a
 * stray native submit cannot put them in a URL). The photos are resized into
 * in-memory Blobs, the picked originals are dropped from the file inputs at
 * once, and everything is released after a successful submit or when the page
 * is left. Only the masked forms the server sends back are ever shown.
 */

/* -------------------------------------------------------------------------
   Account state: the latest `wallet` and KYC status, in memory only
   ------------------------------------------------------------------------- */

var account = {
  known: false,
  wallet: null,
  /* null: never submitted, otherwise kyc.status ("0" | "1" | "2") */
  kyc: null,
};

function rulesOn(wallet) {
  return !!(wallet && wallet.kycRules);
}

/*
 * limits.kycRequiredAbove: without KYC, a single transaction over this is
 * refused with kyc_required. It is what remains of the limits when an
 * administrator has set custom ones on the locker (wallet.customLimits,
 * API-CHANGES-FOR-APP.md §20). null when there is no such rule.
 */
function kycThreshold(wallet) {
  var above = wallet && wallet.limits ? wallet.limits.kycRequiredAbove : null;
  if (above === null || above === undefined || isNaN(+above) || +above <= 0) return null;
  return +above;
}

/*
 * Fed every `userinfo`, `kycStatus` and `submitKyc` reply. A reply that is not
 * a signed-in one (expired token, terminated account) or null (sign-out)
 * clears the state, so one customer's limits never gate the next one's.
 */
export function noteAccount(data) {
  var signedIn = !!data && (data.isLoggedin == 'true' || data.status === 'success');
  account.known = signedIn;
  account.wallet = signedIn && data.wallet && typeof data.wallet === 'object' ? data.wallet : null;
  account.kyc = signedIn && data.kyc ? String(data.kyc.status) : null;
  paintAccountEntries();
}

var KYC_BADGE = {
  none: { text: 'Not done', tone: 'brand', sub: 'Raise your wallet limits' },
  '0': { text: 'In review', tone: 'gold', sub: 'We are checking your documents' },
  '1': { text: 'Verified', tone: 'green', sub: 'Your identity is verified' },
  '2': { text: 'Rejected', tone: 'danger', sub: 'Not approved, tap to see why' },
};

/*
 * The KYC and Wallet limits rows in the profile popup and the link in the
 * wallet breakdown sheet. With the limits switched off (kycRules false) nothing
 * here prompts for KYC; an existing KYC still shows its status.
 */
function paintAccountEntries() {
  var wallet = account.wallet;
  var rules = rulesOn(wallet);
  var waived = !!(wallet && wallet.kycWaived);
  var hasKyc = account.kyc !== null;
  var showKyc = account.known && (hasKyc || (rules && !waived));
  var showLimits = account.known && rules;

  var badge = KYC_BADGE[hasKyc ? account.kyc : 'none'] || KYC_BADGE.none;
  /* dom7's toggleClass has no `force` argument, hence add/remove */
  $$('.kyc-entry')[showKyc ? 'removeClass' : 'addClass']('hidden');
  $$('.kyc-entry__sub').text(badge.sub);
  $$('.kyc-entry__badge')
    .text(badge.text)
    .attr('data-tone', badge.tone);

  $$('.limits-entry')[showLimits ? 'removeClass' : 'addClass']('hidden');
  $$('.limits-entry__sub').text(
    wallet && wallet.customLimits ? 'Custom limits'
      : wallet && wallet.tier === 'full' ? 'Full-KYC wallet' : 'Minimum-KYC wallet'
  );

  $$('.account-entries')[showKyc || showLimits ? 'removeClass' : 'addClass']('hidden');
}

/* -------------------------------------------------------------------------
   Gating and refusals
   ------------------------------------------------------------------------- */

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

function openKyc(app) {
  var view = app.views.main || app.views.current;
  if (view && view.router) view.router.navigate('/kyc/');
}

/* `text` is HTML: F7 dialogs set it as innerHTML. Escape anything from the server. */
function kycDialog(app, title, text) {
  var pending = account.kyc === '0';
  app.dialog.create({
    title: title,
    text: text,
    buttons: [
      { text: pending ? 'OK' : 'Not now' },
      {
        text: pending ? 'View status' : account.kyc === '2' ? 'Fix KYC' : 'Complete KYC',
        bold: true,
        onClick: function () { openKyc(app); },
      },
    ],
  }).open();
}

var LOCKED_ACTION = {
  transfer: 'Sending money to other wallets',
  withdraw: 'Withdrawing to a bank account',
};

function explainLocked(app, kind) {
  var what = LOCKED_ACTION[kind] + ' needs a KYC-verified wallet.';
  if (account.kyc === '0') {
    kycDialog(app, 'KYC under review', what + ' We are checking your documents and will unlock it once they are verified.');
  } else if (account.kyc === '2') {
    kycDialog(app, 'KYC needs attention', what + ' Your last KYC was not approved. See why and submit it again.');
  } else {
    kycDialog(app, 'Complete your KYC', what + ' It takes a few minutes with your PAN card, Aadhaar card and a selfie.');
  }
}

/*
 * beforeEnter for /withdraw/ and /money-transfer/. With the limits on and
 * the action not allowed at this level, the customer gets an explanation and
 * a way to KYC instead of a form that can only fail. Nothing known yet (a deep
 * link before the first userinfo) lets them through; the server still refuses
 * with kyc_required, which walletRefusal() handles.
 */
export function walletGate(kind) {
  var flag = kind === 'transfer' ? 'transfersAllowed' : 'withdrawalsAllowed';
  return function (to, from, resolve, reject) {
    var wallet = account.wallet;
    if (!rulesOn(wallet) || !wallet.limits || wallet.limits[flag] !== false) {
      resolve();
      return;
    }
    var router = this;
    reject();
    /* Next tick, as requireSignIn does: the router is still unwinding. */
    setTimeout(function () { explainLocked(router.app, kind); }, 0);
  };
}

/*
 * `deposit`, `withdraw`, `transferMoneyWallet` and placing a recharge can be
 * refused for the wallet's KYC level (HTTP 400). Returns true when it has told
 * the customer, so the call site skips its own toast. Nothing was debited in
 * either case.
 */
export function walletRefusal(app, data) {
  if (!data || data.status === 'success') return false;
  if (data.code === 'kyc_required') {
    kycDialog(app, account.kyc === '0' ? 'KYC under review' : 'KYC needed',
      escapeHtml(data.msg || 'This needs a KYC-verified wallet.'));
    return true;
  }
  if (data.code === 'wallet_limit') {
    app.dialog.alert(escapeHtml(data.msg || 'This is over your wallet limit.'), 'Wallet limit');
    return true;
  }
  return false;
}

function isAmount(value) {
  return value !== null && value !== undefined && value !== '' && !isNaN(+value);
}

/*
 * What a deposit can be right now. The server applies two limits, in this
 * order (website/app/php/lockers.php, deposit):
 *
 *   1. the wallet limits by KYC level, while switched on -- the balance cap
 *      and money added a month and a financial year (₹10,000 a month without
 *      KYC). From userinfo.wallet.
 *   2. subscribeInfo -> depositLimit: a DAILY limit (₹10,000 without KYC).
 *
 * Without KYC the monthly limit is the one that bites, so quoting the daily
 * figure alone overstated what could be added. This picks whichever leaves
 * the least room. Deposits still being verified count on the server but are
 * not in `used`, so the figure is a ceiling, and the hint says so.
 *
 * Returns { max, label, hint, msg, kycHelps } or null when nothing is known.
 */
export function depositAllowance(dailyLimit) {
  var wallet = account.wallet;
  var minimum = rulesOn(wallet) && wallet.tier === 'minimum' && !wallet.kycWaived;
  var custom = rulesOn(wallet) && !!wallet.customLimits;
  var options = [];

  /* Custom locker limits: no caps, but without KYC one deposit cannot be
     over kycRequiredAbove */
  var above = custom && minimum ? kycThreshold(wallet) : null;
  if (above !== null) {
    options.push({
      max: above,
      kycHelps: true,
      perDeposit: true,
      label: 'Largest deposit without KYC',
      hint: 'Up to <strong>' + money(above) + '</strong> per deposit without KYC.',
      msg: 'Single transactions over ' + money(above) + ' need a KYC-verified wallet.',
    });
  }

  if (rulesOn(wallet) && wallet.limits && !custom) {
    var limits = wallet.limits;
    var used = wallet.used || {};
    if (isAmount(limits.monthlyAdd)) {
      var monthCap = +limits.monthlyAdd;
      var monthUsed = +used.addedThisMonth || 0;
      var monthLeft = Math.max(0, monthCap - monthUsed);
      options.push({
        max: monthLeft,
        label: monthUsed > 0 ? 'Left to add this month' : 'Monthly limit on money added',
        hint: monthLeft === 0
          ? 'You have reached your <strong>' + money(monthCap) + '</strong> monthly limit.'
          : monthUsed > 0
            ? '<strong>' + money(monthLeft) + '</strong> left of your ' + money(monthCap) + ' monthly limit.'
            : 'Up to <strong>' + money(monthCap) + '</strong> a month.',
        msg: 'You can add up to ' + money(monthCap) + ' a month' +
          (monthUsed > 0 ? ', and have ' + money(monthLeft) + ' left this month.' : '.'),
      });
    }
    if (isAmount(limits.yearlyAdd)) {
      var yearCap = +limits.yearlyAdd;
      var yearLeft = Math.max(0, yearCap - (+used.addedThisYear || 0));
      options.push({
        max: yearLeft,
        label: 'Left to add this financial year',
        hint: '<strong>' + money(yearLeft) + '</strong> left of your ' + money(yearCap) + ' yearly limit (April to March).',
        msg: 'You can add up to ' + money(yearCap) + ' a financial year (April to March), and have ' + money(yearLeft) + ' left.',
      });
    }
    if (isAmount(limits.maxBalance)) {
      var room = Math.max(0, +limits.maxBalance - (+used.balance || 0));
      options.push({
        max: room,
        label: 'Room left in your wallet',
        hint: 'Your wallet can hold <strong>' + money(room) + '</strong> more (up to ' + money(+limits.maxBalance) + ').',
        msg: 'Your wallet can hold up to ' + money(+limits.maxBalance) + ', so you can add ' + money(room) + ' more.',
      });
    }
  }
  if (isAmount(dailyLimit)) {
    options.push({
      max: +dailyLimit,
      label: 'Daily deposit limit',
      hint: 'Up to <strong>' + money(+dailyLimit) + '</strong> a day.',
      msg: 'You can deposit up to ' + money(+dailyLimit) + ' a day.',
    });
  }
  if (!options.length) return null;

  /* Least room wins; on a tie the earlier (longer-period) limit is the one
     worth naming */
  var rule = options[0];
  options.forEach(function (option) { if (option.max < rule.max) rule = option; });
  /* KYC raises the standard limits of a minimum-KYC wallet, never a figure
     an administrator set */
  if (rule.kycHelps === undefined) rule.kycHelps = minimum && !custom;
  if (!rule.perDeposit) rule.hint += ' Deposits still being verified count towards it.';
  if (rule.kycHelps) rule.msg += ' Complete your KYC to add more.';
  return rule;
}

/* The over-limit answer, before the screenshot is uploaded */
export function depositRefusal(app, rule) {
  return walletRefusal(app, {
    status: 'error',
    code: rule.kycHelps ? 'kyc_required' : 'wallet_limit',
    msg: rule.msg,
  });
}

/* -------------------------------------------------------------------------
   Validation -- mirrors of the server's rules (API-KYC.md §3)
   ------------------------------------------------------------------------- */

/* As printed on the PAN card: 2-80 characters, a letter, no digits or < > @ */
export function nameError(value, what) {
  var name = (value || '').trim();
  if (!name) return 'Enter ' + what + ' as printed on the PAN card.';
  if (name.length < 2 || name.length > 80) return 'This must be 2 to 80 characters long.';
  if (/[0-9<>@]/.test(name) || !/[A-Za-z]/.test(name)) {
    return 'Use letters only, as printed on the PAN card.';
  }
  return null;
}

/* DD/MM/YYYY, a real date, age 18 to 100 */
export function dobError(value, now) {
  var match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((value || '').trim());
  if (!value) return 'Select your date of birth.';
  if (!match) return 'Enter a valid date of birth.';
  var day = +match[1];
  var month = +match[2];
  var year = +match[3];
  var date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return 'Enter a valid date of birth.';
  }
  var today = now || new Date();
  var age = today.getFullYear() - year;
  if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) age -= 1;
  if (age < 18) return 'You must be 18 or older to complete KYC.';
  if (age > 100) return 'Please check your date of birth.';
  return null;
}

export function normalizePan(value) {
  return (value || '').replace(/\s+/g, '').toUpperCase();
}

/* 10 characters ABCDE1234F, a personal PAN (4th character P) */
export function panError(value) {
  var pan = normalizePan(value);
  if (!pan) return 'Enter your PAN.';
  if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return 'Enter the 10-character PAN, like ABCDE1234F.';
  if (pan.charAt(3) !== 'P') {
    return 'This is not a personal PAN. Use the PAN card issued to you: its 4th letter is P.';
  }
  return null;
}

export function normalizeAadhaar(value) {
  return (value || '').replace(/[\s-]/g, '');
}

/* Verhoeff, the same checksum the server uses (and UIDAI issues numbers with) */
var VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
var VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

export function verhoeffValid(digits) {
  var check = 0;
  for (var i = 0; i < digits.length; i += 1) {
    var digit = +digits.charAt(digits.length - 1 - i);
    check = VERHOEFF_D[check][VERHOEFF_P[i % 8][digit]];
  }
  return check === 0;
}

/* 12 digits, first digit 2-9, valid checksum */
export function aadhaarError(value) {
  var aadhaar = normalizeAadhaar(value);
  if (!aadhaar) return 'Enter your Aadhaar number.';
  if (!/^[0-9]{12}$/.test(aadhaar)) return 'Enter the 12-digit Aadhaar number.';
  if (!/^[2-9]/.test(aadhaar)) return 'An Aadhaar number cannot start with 0 or 1.';
  if (!verhoeffValid(aadhaar)) return 'This Aadhaar number is not valid. Please check it against your card.';
  return null;
}

/* -------------------------------------------------------------------------
   Photos -- resized before upload (API-KYC.md §3: ~1600px, JPEG ~80)
   ------------------------------------------------------------------------- */

/*
 * data: URLs, not blob: ones -- index.html's Content-Security-Policy allows
 * `data:` but not `blob:`, so an <img> or background pointing at a blob URL is
 * refused outright.
 */
export function readAsDataUrl(blob) {
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();
    reader.onload = function () { resolve(reader.result); };
    reader.onerror = function () { reject(new Error('unreadable')); };
    reader.readAsDataURL(blob);
  });
}

function loadImage(file) {
  return readAsDataUrl(file).then(function (url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('unreadable')); };
      img.src = url;
    });
  });
}

function canvasToJpeg(canvas, quality) {
  return new Promise(function (resolve) {
    if (canvas.toBlob) {
      canvas.toBlob(function (blob) { resolve(blob); }, 'image/jpeg', quality);
      return;
    }
    var data = atob(canvas.toDataURL('image/jpeg', quality).split(',')[1]);
    var bytes = new Uint8Array(data.length);
    for (var i = 0; i < data.length; i += 1) bytes[i] = data.charCodeAt(i);
    resolve(new Blob([bytes], { type: 'image/jpeg' }));
  });
}

/*
 * Orientation: WebViews since Chrome 81 draw an <img> with its EXIF rotation
 * applied, so the canvas -- and the JPEG made from it -- is upright. Android 7+
 * (minSdk 24) keeps its WebView updated through the Play Store.
 */
function encodeJpeg(img, longSide, quality) {
  var width = img.naturalWidth || img.width;
  var height = img.naturalHeight || img.height;
  var scale = Math.min(1, longSide / Math.max(width, height));
  var canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  var ctx = canvas.getContext('2d');
  /* A transparent PNG would otherwise turn black in a JPEG */
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvasToJpeg(canvas, quality);
}

var ENCODE_STEPS = [[1600, 0.8], [1600, 0.7], [1280, 0.7], [1024, 0.65], [800, 0.6]];

/* Resolves to a JPEG Blob of at most maxBytes, stepping down until it fits. */
export function prepareImage(file, maxBytes) {
  return loadImage(file).then(function (img) {
    var step = 0;
    function attempt() {
      var size = ENCODE_STEPS[step];
      step += 1;
      return encodeJpeg(img, size[0], size[1]).then(function (blob) {
        if (blob && blob.size > 0 && blob.size <= maxBytes) return blob;
        if (step < ENCODE_STEPS.length) return attempt();
        throw new Error('too-large');
      });
    }
    return attempt();
  });
}

/* -------------------------------------------------------------------------
   Shared page plumbing
   ------------------------------------------------------------------------- */

function kycUrl(apiBase) {
  return String(apiBase || '').replace(/\/+$/, '') + '/app/php/kyc.php';
}

/* api.js has already told the customer about these: an expired session,
   a block, a rate limit. */
function handledGlobally(body) {
  return !!body && (body.status === 'forbidden' || body.code === 'blocked' || body.code === 'rate_limited');
}

function fetchStatus(app, apiBase, done) {
  var $ = window.jQuery;
  app.dialog.progress('Loading...');
  $.ajax({
    url: kycUrl(apiBase),
    type: 'POST',
    dataType: 'json',
    cache: false,
    crossDomain: true,
    data: { kycStatus: 1, token: localStorage.getItem('authToken') },
    success: function (body) {
      app.dialog.close();
      if (body && body.status === 'success') {
        noteAccount(body);
        done(body, null);
      } else {
        done(null, handledGlobally(body) ? '' : (body && body.msg) || 'Could not load your KYC.');
      }
    },
    error: function (xhr) {
      app.dialog.close();
      done(null, xhr && xhr.status === 503
        ? 'KYC is unavailable right now, please try again later.'
        : 'No internet connection!');
    },
  });
}

function show(el, visible) {
  if (el) el.classList.toggle('hidden', !visible);
}

/* The error state shared by both pages: the message plus a retry. */
function paintLoadError(root, message, retry) {
  var box = root.querySelector('.kyc-load-error');
  if (!box) return;
  box.querySelector('.ypw-empty__text').textContent = message || 'Please try again.';
  show(box, true);
  var button = box.querySelector('.kyc-retry');
  button.onclick = function (e) {
    e.preventDefault();
    show(box, false);
    retry();
  };
}

/* Wallet limits are rupees whatever the customer's country (RBI's rules) */
var rupees = null;
function money(value) {
  var n = Number(value) || 0;
  try {
    rupees = rupees || new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2,
    });
    return rupees.format(n);
  } catch (e) {
    return '₹' + n;
  }
}

/* -------------------------------------------------------------------------
   /kyc/
   ------------------------------------------------------------------------- */

var FIELDS = [
  'fullName', 'fatherName', 'dob', 'pan', 'aadhaar',
  'panDoc', 'aadhaarFrontDoc', 'aadhaarBackDoc', 'photoDoc', 'consent',
];

var PHOTO_MISSING = {
  panDoc: 'Add a photo of the front of your PAN card.',
  aadhaarFrontDoc: 'Add a photo of the front of your Aadhaar card.',
  aadhaarBackDoc: 'Add a photo of the back of your Aadhaar card.',
  photoDoc: 'Add a selfie.',
};

/* Headroom in the whole-request budget for the text fields and multipart framing */
var FORM_OVERHEAD = 64 * 1024;

function stateCopy(state, wallet) {
  var rules = rulesOn(wallet);
  /* Custom locker limits have no caps for KYC to raise, only a threshold */
  var custom = rules && !!wallet.customLimits;
  var above = custom ? kycThreshold(wallet) : null;
  switch (state) {
    case '0':
      return {
        tone: 'gold',
        title: 'We are checking your documents',
        text: 'Your KYC is under review. Open this screen again to see the result.',
      };
    case '1':
      return {
        tone: 'green',
        title: 'KYC verified',
        text: rules && !custom ? 'Your wallet has the full-KYC limits.' : 'Your identity has been verified.',
      };
    case '2':
      return {
        tone: 'danger',
        title: 'KYC not approved',
        text: 'Please fix the problem below and submit your KYC again.',
      };
    case 'waived':
      return {
        tone: 'green',
        title: 'Full limits enabled',
        text: 'Your wallet has the full limits. You do not need to complete KYC.',
      };
    default:
      return {
        tone: 'brand',
        title: 'Complete your KYC',
        text: above !== null
          ? 'Complete your KYC to make transactions over ' + money(above) + '. You will need your PAN card, Aadhaar card and a selfie.'
          : rules && !custom
            ? 'Complete your KYC to raise your wallet limits. You will need your PAN card, Aadhaar card and a selfie.'
            : 'Verify your identity with your PAN card, Aadhaar card and a selfie.',
      };
  }
}

function countMatching(text, pattern) {
  var n = 0;
  for (var i = 0; i < text.length; i += 1) {
    if (pattern.test(text.charAt(i))) n += 1;
  }
  return n;
}

/*
 * Reformats as the customer types (uppercasing, grouping) without throwing the
 * caret to the end: it lands after the same number of `significant`
 * characters it was after before.
 */
function reformat(input, format, significant) {
  var before = input.value;
  var caret = input.selectionStart == null ? before.length : input.selectionStart;
  var kept = countMatching(before.slice(0, caret), significant);
  var after = format(before);
  if (after === before) return;
  input.value = after;
  var pos = 0;
  var seen = 0;
  while (pos < after.length && seen < kept) {
    if (significant.test(after.charAt(pos))) seen += 1;
    pos += 1;
  }
  try { input.setSelectionRange(pos, pos); } catch (e) { /* not focused */ }
}

function formatPan(value) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
}

function formatAadhaar(value) {
  var digits = value.replace(/\D/g, '').slice(0, 12);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}

export function initKycPage(app, page, apiBase) {
  var root = page.el;
  var form = root.querySelector('.ypw-kyc__form');
  var content = root.querySelector('.page-content');
  var submitBtn = root.querySelector('.kyc-submit');
  var banner = root.querySelector('.kyc-form-error');
  var inputs = {
    fullName: root.querySelector('#kycFullName'),
    fatherName: root.querySelector('#kycFatherName'),
    dob: root.querySelector('#kycDob'),
    pan: root.querySelector('#kycPan'),
    aadhaar: root.querySelector('#kycAadhaar'),
    consent: root.querySelector('#kycConsent'),
  };

  var maxPhoto = 5 * 1024 * 1024;
  var maxTotal = 8 * 1024 * 1024;
  var photos = {};
  var preparing = {};
  var sending = false;
  var gone = false;

  var calendar = createDobCalendar(app, inputs.dob, 100);

  function fieldEl(name) {
    return FIELDS.indexOf(name) === -1 ? null : root.querySelector('.ypw-field[data-field="' + name + '"]');
  }

  function markInvalid(field, message) {
    var el = typeof field === 'string' ? fieldEl(field) : field;
    if (!el) return null;
    var msg = el.querySelector('.ypw-error');
    if (msg) msg.textContent = message;
    el.classList.add('is-invalid');
    return el;
  }

  function clearInvalid(field) {
    var el = typeof field === 'string' ? fieldEl(field) : field;
    if (el) el.classList.remove('is-invalid');
  }

  function reveal(el) {
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      el.scrollIntoView();
    }
    var input = el.querySelector('input:not([type="file"]):not([type="checkbox"]):not([readonly])');
    if (input) input.focus();
  }

  function showBanner(message) {
    banner.textContent = message || '';
    show(banner, !!message);
    if (message) reveal(banner);
  }

  /* --- photos --------------------------------------------------------- */

  function photoBudget() {
    return Math.max(256 * 1024, Math.min(maxPhoto, Math.floor((maxTotal - FORM_OVERHEAD) / 4)));
  }

  /* `preview` is the resized JPEG as a data: URL (see readAsDataUrl) */
  function setPhoto(key, blob, preview) {
    var slot = fieldEl(key);
    var tile = slot.querySelector('.ypw-kyc-photo__preview');
    if (!blob) {
      delete photos[key];
      tile.style.backgroundImage = '';
      slot.classList.remove('has-photo');
      return;
    }
    photos[key] = { blob: blob };
    tile.style.backgroundImage = 'url("' + preview + '")';
    slot.classList.add('has-photo');
    clearInvalid(slot);
  }

  function clearPhotos() {
    Object.keys(photos).forEach(function (key) { setPhoto(key, null); });
    preparing = {};
  }

  Array.prototype.forEach.call(root.querySelectorAll('.ypw-kyc-photo__input'), function (input) {
    input.addEventListener('change', function () {
      var slot = input.closest('.ypw-kyc-photo');
      var key = slot.getAttribute('data-field');
      var file = input.files && input.files[0];
      /* The original stays in memory only as long as it takes to resize it */
      input.value = '';
      if (!file) return;
      if (file.type && !/^image\//i.test(file.type)) {
        markInvalid(slot, 'Choose a photo (JPG, PNG or WEBP).');
        return;
      }
      var ticket = {};
      preparing[key] = ticket;
      slot.classList.add('is-busy');
      clearInvalid(slot);
      prepareImage(file, photoBudget()).then(function (blob) {
        return readAsDataUrl(blob).then(function (preview) {
          if (gone || preparing[key] !== ticket) return;
          setPhoto(key, blob, preview);
        });
      }).catch(function (err) {
        if (gone || preparing[key] !== ticket) return;
        markInvalid(slot, err && err.message === 'too-large'
          ? 'This photo is too large. Please take it again.'
          : 'This photo could not be read. Please choose a JPG, PNG or WEBP photo.');
      }).then(function () {
        /* A newer pick for this tile keeps it busy; anything else clears it */
        if (preparing[key] === ticket) delete preparing[key];
        if (!preparing[key]) slot.classList.remove('is-busy');
      });
    });
  });

  /* --- typing --------------------------------------------------------- */

  inputs.pan.addEventListener('input', function () {
    reformat(inputs.pan, formatPan, /[A-Za-z0-9]/);
  });
  inputs.aadhaar.addEventListener('input', function () {
    reformat(inputs.aadhaar, formatAadhaar, /[0-9]/);
  });
  inputs.consent.addEventListener('change', function () {
    if (inputs.consent.checked) clearInvalid('consent');
  });

  /* --- render --------------------------------------------------------- */

  function paintDetails(kyc) {
    var list = root.querySelector('.kyc-details__rows');
    list.innerHTML = '';
    [
      ['Full name', kyc.fullName],
      ["Father's name", kyc.fatherName],
      ['Date of birth', kyc.dob],
      ['PAN', kyc.pan],
      ['Aadhaar', kyc.aadhaar],
      ['Submitted', kyc.submittedAt],
      ['Reviewed', kyc.reviewedAt],
    ].forEach(function (row) {
      if (row[1] === null || row[1] === undefined || row[1] === '') return;
      var line = document.createElement('div');
      line.className = 'ypw-payee-card__row';
      var label = document.createElement('span');
      label.textContent = row[0];
      var value = document.createElement('b');
      value.textContent = String(row[1]);
      line.appendChild(label);
      line.appendChild(value);
      list.appendChild(line);
    });
  }

  function render(body) {
    var kyc = body.kyc || null;
    var wallet = body.wallet || null;
    var waived = !kyc && !!(wallet && wallet.kycWaived);
    var state = waived ? 'waived' : kyc ? String(kyc.status) : 'none';

    if (body.upload) {
      maxPhoto = +body.upload.maxPhotoBytes || maxPhoto;
      maxTotal = +body.upload.maxTotalBytes || maxTotal;
    }

    var copy = stateCopy(state, wallet);
    var status = root.querySelector('.ypw-kyc-status');
    status.setAttribute('data-tone', copy.tone);
    status.setAttribute('data-state', state);
    status.querySelector('.ypw-kyc-status__title').textContent = copy.title;
    status.querySelector('.ypw-kyc-status__text').textContent = copy.text;
    show(status, true);

    var reason = kyc && state === '2' ? String(kyc.rejectReason || '').trim() : '';
    root.querySelector('.kyc-reason__text').textContent = reason;
    show(root.querySelector('.kyc-reason'), !!reason);

    var details = root.querySelector('.kyc-details');
    if (kyc && (state === '0' || state === '1')) paintDetails(kyc);
    show(details, !!kyc && (state === '0' || state === '1'));

    /* canSubmit already folds in kycAvailable; an administrator's waiver
       means the customer is not asked at all. */
    var canSubmit = !!body.canSubmit && !waived;
    show(form, canSubmit);
    show(root.querySelector('.kyc-unavailable'),
      !canSubmit && !waived && body.kycAvailable === false && (state === 'none' || state === '2'));

    /* A rejected customer is resubmitting the same person: keep the parts
       that are not masked, so only the problem needs fixing. */
    if (canSubmit && kyc && state === '2') {
      if (!inputs.fullName.value && kyc.fullName) inputs.fullName.value = kyc.fullName;
      if (!inputs.fatherName.value && kyc.fatherName) inputs.fatherName.value = kyc.fatherName;
      if (!inputs.dob.value && kyc.dob && !dobError(kyc.dob)) calendar.setValue([parseDob(kyc.dob)]);
    }

    show(root.querySelector('.kyc-limits-link'), rulesOn(wallet));
  }

  function parseDob(value) {
    var parts = value.split('/');
    return new Date(+parts[2], +parts[1] - 1, +parts[0]);
  }

  function load() {
    show(root.querySelector('.kyc-load-error'), false);
    fetchStatus(app, apiBase, function (body, error) {
      if (gone) return;
      if (body) {
        render(body);
      } else {
        paintLoadError(root, error, load);
      }
    });
  }

  /* --- submit --------------------------------------------------------- */

  /* Everything wrong is marked at once; the server reports one at a time. */
  function validate() {
    var problems = [
      ['fullName', nameError(inputs.fullName.value, 'your full name')],
      ['fatherName', nameError(inputs.fatherName.value, "your father's name")],
      ['dob', dobError(inputs.dob.value)],
      ['pan', panError(inputs.pan.value)],
      ['aadhaar', aadhaarError(inputs.aadhaar.value)],
      /* The grid's order: PAN and selfie, then Aadhaar front and back */
      ['panDoc', photos.panDoc ? null : PHOTO_MISSING.panDoc],
      ['photoDoc', photos.photoDoc ? null : PHOTO_MISSING.photoDoc],
      ['aadhaarFrontDoc', photos.aadhaarFrontDoc ? null : PHOTO_MISSING.aadhaarFrontDoc],
      ['aadhaarBackDoc', photos.aadhaarBackDoc ? null : PHOTO_MISSING.aadhaarBackDoc],
      ['consent', inputs.consent.checked ? null : 'Tick the box to agree to share your PAN and Aadhaar details.'],
    ];
    var first = null;
    problems.forEach(function (problem) {
      if (problem[1]) {
        var el = markInvalid(problem[0], problem[1]);
        if (!first) first = el;
      } else {
        clearInvalid(problem[0]);
      }
    });
    if (first) reveal(first);
    return !first;
  }

  function setSending(on) {
    sending = on;
    submitBtn.classList.toggle('disabled', on);
  }

  function resetForm() {
    form.reset();
    inputs.dob.value = '';
    clearPhotos();
    FIELDS.forEach(clearInvalid);
    showBanner('');
  }

  function onReply(body, progress) {
    progress.close();
    setSending(false);
    if (gone) return;

    if (body && body.status === 'success') {
      resetForm();
      noteAccount(body);
      render(body);
      content.scrollTop = 0;
      app.toast.show({ text: body.msg || 'KYC submitted.', closeTimeout: 3500 });
      return;
    }
    if (!body || handledGlobally(body)) return;

    if (body.code === 'kyc_pending' || body.code === 'kyc_verified') {
      app.dialog.alert(escapeHtml(body.msg), 'KYC', load);
      return;
    }
    if (body.code === 'unavailable') {
      showBanner(body.msg || 'KYC is unavailable right now, please try again later.');
      return;
    }
    /* invalid, pan_taken, aadhaar_taken, too_large: the server names the
       input when there is one; otherwise it is a banner by the button. */
    var el = body.field ? markInvalid(body.field, body.msg) : null;
    if (el) {
      if (body.code === 'too_large' && photos[body.field]) setPhoto(body.field, null);
      reveal(el);
    } else {
      showBanner(body.msg || 'Could not submit your KYC. Please try again.');
    }
  }

  function onFail(xhr, progress) {
    progress.close();
    setSending(false);
    if (gone) return;
    var status = xhr && xhr.status;
    if (status === 413) {
      /* Refused before PHP saw it (a proxy limit), so there is no JSON body */
      showBanner('Your photos are too large to send. Please take them again and retry.');
    } else if (status === 503) {
      showBanner('KYC is unavailable right now, please try again later.');
    } else if (status >= 500) {
      showBanner('Something went wrong, please try again.');
    } else {
      app.toast.show({ text: 'No internet connection!', closeTimeout: 3000 });
    }
  }

  function submit() {
    if (sending) return;
    showBanner('');
    if (Object.keys(preparing).length) {
      app.toast.show({ text: 'Your photos are still being prepared, one moment.', closeTimeout: 2500 });
      return;
    }
    if (!validate()) return;

    var total = FORM_OVERHEAD;
    Object.keys(photos).forEach(function (key) { total += photos[key].blob.size; });
    if (total > maxTotal) {
      showBanner('Your photos are too large together. Please take them again and retry.');
      return;
    }

    var fd = new FormData();
    fd.append('submitKyc', '1');
    fd.append('token', localStorage.getItem('authToken') || '');
    fd.append('fullName', inputs.fullName.value.trim());
    fd.append('fatherName', inputs.fatherName.value.trim());
    fd.append('dob', inputs.dob.value.trim());
    fd.append('pan', normalizePan(inputs.pan.value));
    fd.append('aadhaar', normalizeAadhaar(inputs.aadhaar.value));
    fd.append('consent', inputs.consent.checked ? '1' : '');
    fd.append('panDoc', photos.panDoc.blob, 'pan.jpg');
    fd.append('aadhaarFrontDoc', photos.aadhaarFrontDoc.blob, 'aadhaar-front.jpg');
    fd.append('aadhaarBackDoc', photos.aadhaarBackDoc.blob, 'aadhaar-back.jpg');
    fd.append('photoDoc', photos.photoDoc.blob, 'selfie.jpg');

    setSending(true);
    var progress = app.dialog.progress('Uploading your documents', 0);
    var $ = window.jQuery;
    /* Through the wrapped $.ajax (api.js), so a 4xx arrives at success with its
       body. Content-Type is left to the browser for the multipart boundary. */
    $.ajax({
      url: kycUrl(apiBase),
      type: 'POST',
      data: fd,
      processData: false,
      contentType: false,
      dataType: 'json',
      crossDomain: true,
      xhr: function () {
        var xhr = $.ajaxSettings.xhr();
        if (xhr.upload) {
          xhr.upload.addEventListener('progress', function (e) {
            if (!e.lengthComputable) return;
            var pct = Math.round((e.loaded / e.total) * 100);
            progress.setProgress(pct);
            if (pct >= 100) progress.setTitle('Submitting');
          });
        }
        return xhr;
      },
      success: function (body) { onReply(body, progress); },
      error: function (xhr) { onFail(xhr, progress); },
    });
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    submit();
  });

  root.__kycDestroy = function () {
    gone = true;
    clearPhotos();
    form.reset();
    if (calendar && calendar.destroy) calendar.destroy();
  };

  load();
}

export function destroyKycPage(page) {
  if (page && page.el && page.el.__kycDestroy) {
    page.el.__kycDestroy();
    page.el.__kycDestroy = null;
  }
}

/* -------------------------------------------------------------------------
   /wallet-limits/
   ------------------------------------------------------------------------- */

/* [label, used key, limit key, note]. A null limit is not drawn (API-KYC.md §5). */
var LIMIT_BARS = [
  ['Wallet balance', 'balance', 'maxBalance', 'Money and cashback wallets together'],
  ['Added this month', 'addedThisMonth', 'monthlyAdd', ''],
  ['Added this year', 'addedThisYear', 'yearlyAdd', 'Financial year, April to March'],
  ['Spent this month', 'spentThisMonth', 'monthlySpend', ''],
  ['Sent to other wallets this month', 'sentThisMonth', 'monthlyTransfers', ''],
  ['Withdrawn this month', 'withdrawnThisMonth', 'monthlyWithdrawals', ''],
];

function limitRow(label, used, limit, note) {
  var ratio = limit > 0 ? Math.min(1, Math.max(0, used / limit)) : 1;
  var left = Math.max(0, limit - used);
  var row = document.createElement('div');
  row.className = 'ypw-meter';
  if (ratio >= 1) row.classList.add('is-full');
  else if (ratio >= 0.8) row.classList.add('is-near');

  var head = document.createElement('div');
  head.className = 'ypw-meter__head';
  var name = document.createElement('span');
  name.className = 'ypw-meter__name';
  name.textContent = label;
  var figure = document.createElement('span');
  figure.className = 'ypw-meter__figure';
  figure.textContent = money(used) + ' of ' + money(limit);
  head.appendChild(name);
  head.appendChild(figure);

  var bar = document.createElement('div');
  bar.className = 'ypw-meter__bar';
  bar.setAttribute('role', 'progressbar');
  bar.setAttribute('aria-label', label);
  bar.setAttribute('aria-valuemin', '0');
  bar.setAttribute('aria-valuemax', String(limit));
  bar.setAttribute('aria-valuenow', String(Math.min(used, limit)));
  var fill = document.createElement('span');
  fill.style.width = (ratio * 100).toFixed(1) + '%';
  bar.appendChild(fill);

  var foot = document.createElement('div');
  foot.className = 'ypw-meter__foot';
  var leftEl = document.createElement('span');
  leftEl.textContent = left > 0 ? money(left) + ' left' : 'Limit reached';
  foot.appendChild(leftEl);
  if (note) {
    var noteEl = document.createElement('span');
    noteEl.textContent = note;
    foot.appendChild(noteEl);
  }

  row.appendChild(head);
  row.appendChild(bar);
  row.appendChild(foot);
  return row;
}

function lockedRow(label, minimum) {
  var row = document.createElement('div');
  row.className = 'ypw-meter ypw-meter--locked';
  var head = document.createElement('div');
  head.className = 'ypw-meter__head';
  var name = document.createElement('span');
  name.className = 'ypw-meter__name';
  name.textContent = label;
  var tag = document.createElement('span');
  tag.className = 'ypw-kyc-badge';
  tag.setAttribute('data-tone', minimum ? 'brand' : 'muted');
  tag.textContent = minimum ? 'Needs KYC' : 'Not available';
  head.appendChild(name);
  head.appendChild(tag);
  row.appendChild(head);
  return row;
}

/*
 * On custom locker limits (wallet.customLimits) KYC raises no cap -- there are
 * none -- but it still unlocks single transactions over kycRequiredAbove. The
 * copy says which of the two KYC does for this customer.
 */
function limitsCta(kyc, wallet) {
  var above = wallet.customLimits ? kycThreshold(wallet) : null;
  var gain = above !== null ? 'make transactions over ' + money(above) : 'raise these limits';
  if (kyc && String(kyc.status) === '0') {
    return {
      tone: 'gold',
      title: 'Your KYC is under review',
      text: above !== null
        ? 'Transactions over ' + money(above) + ' are possible as soon as it is verified.'
        : 'These limits rise as soon as your KYC is verified.',
      button: 'View KYC status',
    };
  }
  if (kyc && String(kyc.status) === '2') {
    return {
      tone: 'danger',
      title: 'Your KYC was not approved',
      text: 'See why, fix it and submit again to ' + gain + '.',
      button: 'See why and resubmit',
    };
  }
  return {
    tone: 'brand',
    title: above !== null ? 'Make larger transactions with KYC' : 'Raise your limits with KYC',
    text: (above !== null ? 'Single transactions over ' + money(above) + ' need a KYC-verified wallet. ' : '') +
      'Verify once with your PAN card, Aadhaar card and a selfie.',
    button: 'Complete KYC',
  };
}

export function initLimitsPage(app, page, apiBase) {
  var root = page.el;
  var gone = false;

  function render(body) {
    var wallet = body.wallet || null;
    var rules = rulesOn(wallet);

    /* Limits switched off: nothing about limits, and no KYC prompt */
    show(root.querySelector('.limits-off'), !rules);
    show(root.querySelector('.limits-on'), rules);
    if (!rules) return;

    var full = wallet.tier === 'full';
    var custom = !!wallet.customLimits;
    var above = kycThreshold(wallet);
    var tier = root.querySelector('.ypw-limits-tier');
    tier.setAttribute('data-tone', full ? 'green' : 'brand');
    tier.querySelector('.ypw-limits-tier__title').textContent = custom
      ? 'Custom limits'
      : full ? 'Full-KYC wallet' : 'Minimum-KYC wallet';
    tier.querySelector('.ypw-limits-tier__text').textContent = custom
      ? 'Limits set for your account replace the standard ones.' +
        (!full && above !== null ? ' Single transactions over ' + money(above) + ' need a KYC-verified wallet.' : '')
      : full
        ? (wallet.kycWaived && !wallet.kycVerified
          ? 'Full limits have been enabled for your account.'
          : 'Your KYC is verified, so your wallet has the full limits.')
        : 'This is a wallet without KYC. Its limits are lower.';

    /* Custom limits with no KYC threshold: KYC would change nothing here */
    var offerKyc = !full && !wallet.kycWaived && (!custom || above !== null);
    var cta = root.querySelector('.limits-cta');
    show(cta, offerKyc);
    if (offerKyc) {
      var copy = limitsCta(body.kyc, wallet);
      cta.setAttribute('data-tone', copy.tone);
      cta.querySelector('.ypw-callout__title').textContent = copy.title;
      cta.querySelector('.ypw-callout__text').textContent = copy.text;
      cta.querySelector('.limits-cta__button span').textContent = copy.button;
    }

    var limits = wallet.limits || {};
    var used = wallet.used || {};
    var bars = root.querySelector('.limits-bars');
    bars.innerHTML = '';
    LIMIT_BARS.forEach(function (def) {
      var limit = limits[def[2]];
      if (limit === null || limit === undefined || isNaN(+limit)) return;
      bars.appendChild(limitRow(def[0], +used[def[1]] || 0, +limit, def[3]));
    });
    var capped = bars.children.length > 0;
    show(root.querySelector('.limits-bars-panel'), capped);
    /* Custom limits null every cap: say so rather than show an empty list */
    show(root.querySelector('.limits-uncapped'), !capped);
    show(root.querySelector('.ypw-limits__foot'), capped);

    var locked = root.querySelector('.limits-locked');
    locked.innerHTML = '';
    if (limits.transfersAllowed === false) locked.appendChild(lockedRow('Send money to other wallets', !full));
    if (limits.withdrawalsAllowed === false) locked.appendChild(lockedRow('Withdraw to a bank account', !full));
    if (!full && above !== null) locked.appendChild(lockedRow('Single transactions over ' + money(above), true));
    show(root.querySelector('.limits-locked-panel'), locked.children.length > 0);
  }

  function load() {
    show(root.querySelector('.kyc-load-error'), false);
    fetchStatus(app, apiBase, function (body, error) {
      if (gone) return;
      if (body) render(body);
      else paintLoadError(root, error, load);
    });
  }

  root.__kycDestroy = function () { gone = true; };
  load();
}
